/*! ravelinjs 1.10.0 - https://github.com/unravelin/ravelinjs. Copyright 2025 */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.Ravelin = factory());
})(this, (function () { 'use strict';

  /**
   * CookieJar provides getters/setters for cookies.
   * @class
   * @param {object} cfg The cookiejar configuration.
   * @param {string} [cfg.domain] The domain on which to store cookies.
   * @param {string} [cfg.sameSite] The SameSite attribute to control how cookies are sent in cross-site requests.
   */
  function CookieJar(cfg) {
    this.cfg = cfg;
  }

  /**
   * get returns the value of the cookie of the given name, or undefined.
   * @param {string} name
   * @returns {string|undefined}
   */
  CookieJar.prototype.get = function(name) {
    var cookies = document.cookie.split('; ');
    for (var i = cookies.length-1; i >= 0; i--) {
      var x = cookies[i].split("=");
      if (x[0] === name) {
        return x[1];
      }
    }
    return undefined;
  };

  /**
   * set a cookie on the given domain.
   * @param {object} c The cookie to set.
   * @param {string} c.name The name of the cookie.
   * @param {string} c.value The value to be stored in the cookie.
   * @param {string} [c.path="/"] The path to store the cookie under.
   * @param {Date} [c.expires] The expiry time of the cookie. If not set, the cookie implicitly becomes a session cookie.
   */
  CookieJar.prototype.set = function(c) {
    var cook = c.name + "=" + c.value + ";path=" + (c.path || "/") +
      (c.expires ? ";expires=" + c.expires.toUTCString() : "") +
      (this.cfg.domain ? ";domain=" + this.cfg.domain : "");

    if (typeof(this.samesite) === 'undefined') {
      // Determine the correct value for SameSite.
      if (this.cfg.sameSite) {
        this.samesite = ';SameSite=' + this.cfg.sameSite;
      } else if (hasWebKitSameSiteBug(window.navigator.userAgent)) {
        this.samesite = '';
      } else if (window.location.protocol == 'https:') {
        this.samesite = ';SameSite=None;Secure';
      } else {
        this.samesite = ';SameSite=Lax';
      }

      // Try this samesite value.
      document.cookie = cook + this.samesite;
      if (this.get(c.name) == c.value) {
        // We set the cookie successfully.
        return;
      }
      // The cookie was discarded - avoid using samesite.
      this.samesite = '';
    }

    document.cookie = cook + this.samesite;
  };

  /**
   * hasWebKitSameSiteBug indicates whether a given User-Agent treats
   * SameSite:None on cookies as if it were SameSite:Strict, which is very
   * different. Adapted from the procedure on
   * https://www.chromium.org/updates/same-site/incompatible-clients.
   *
   * @param ua string
   * @returns bool
   */
  function hasWebKitSameSiteBug(ua) {
    if (/\(iP.+; CPU .*OS 12[_\d]*.*\) AppleWebKit\//.test(ua)) {
      return true;
    }
    if (/\(Macintosh;.*Mac OS X 10_14[_\d]*.*\) AppleWebKit\//.test(ua)) {
      if (/^Mozilla\/[\.\d]+ \(Macintosh;.*Mac OS X [_\d]+\) AppleWebKit\/[\.\d]+ \(KHTML, like Gecko\)$/.test(ua)) {
        return true;
      }
      if (/Version\/.* Safari\//.test(ua) && !/Chrom(e|ium)/.test(ua)) {
        return true;
      }
    }
    return false;
  }

  /**
   * @private
   * @module ravelinjs/util
   */

  /**
   * bind wraps a function.
   * @param {Function} fn The function to be called.
   * @param {*} thisArg The 'this' of the invoked fn.
   * @param {...*} [args] Arguments to add to the start of the function call.
   * @returns {Function}
   */
  function bind(fn, thisArg, args) {
    args = Array.prototype.slice.call(arguments, 2);
    if (!Function.prototype.bind) {
      return function () {
        return fn.apply(thisArg, args.concat(Array.prototype.slice.call(arguments)));
      };
    }
    return Function.prototype.bind.apply(fn, [thisArg].concat(args));
  }

  /**
   * A lookup table used for uuid generation. Populated on first usage.
   * @type {string[]}
   * @private
   */
  var _lut;

  /**
   * uuid generates a fresh [UUID v4](https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)).
   *
   * @returns {string}
   */
  function uuid() {
    var d0, d1, d2, d3;

    // try to use the newer, randomer crypto.getRandomValues if available
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues && typeof Int32Array !== 'undefined') {
      var d = new Int32Array(4);
      window.crypto.getRandomValues(d);
      d0 = d[0];
      d1 = d[1];
      d2 = d[2];
      d3 = d[3];
    } else {
      // Generate a random float between 0-1, multiple by 4294967295 (0xffffffff)
      // then round down via bitwise or (|0) so we are left with a random 32bit
      // number. These 4 values are then bitshifted around to produce additional
      // random values.
      d0 = Math.random() * 0xffffffff | 0;
      d1 = Math.random() * 0xffffffff | 0;
      d2 = Math.random() * 0xffffffff | 0;
      d3 = Math.random() * 0xffffffff | 0;
    }

    // Populate our lookup table (_lut) sequentially with hexidecimal strings
    // starting from 00 all the way through to ff, covering the entire 256 hex
    // range.
    if (!_lut) {
      _lut = [];
      for (var i = 0; i < 256; i++) {
        _lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
      }
    }

    // From our 4 random 32 bit values, we take the first 8 bits via bitwise AND
    // against 255 (&0xff), then the next 8 bits via bitwise shift right (>>8) and
    // repeat that 4 times through to get 4 random, 8 bit numbers, which are used
    // to look up the sequentially generated hex characters in our lookup table.
    // There are two interesting numbers here though:
    //
    // - the 15th character will always be a 4, because we bitwise AND against 15
    //   rather than 255 and we bitwise OR against 64 (0x40), producing values in
    //   the range of 64-79, which is the 16 hex values prefixed with a 4 (40
    //   through to 4f)
    //
    // - the 20th character will always be one of 8, 9, a or b because we bitwise
    //   AND against 63 and bitwise OR against 128, producing values in the range
    //   of 128-191, which is the 64 hex values ranging from 80 through to bf
    //
    // This logic almost mirrors the specification of v4 RFC 4122 UUIDs, but omits
    // the `clock_seq_hi_and_reserved` requirement
    // https://tools.ietf.org/html/rfc4122.
    //
    // The result are identifiers of 36 characters, 34 of which are randomly
    // assigned.
    return _lut[d0 & 0xff] + _lut[d0 >> 8 & 0xff] + _lut[d0 >> 16 & 0xff] + _lut[d0 >> 24 & 0xff] + '-' +
      _lut[d1 & 0xff] + _lut[d1 >> 8 & 0xff] + '-' + _lut[d1 >> 16 & 0x0f | 0x40] + _lut[d1 >> 24 & 0xff] + '-' +
      _lut[d2 & 0x3f | 0x80] + _lut[d2 >> 8 & 0xff] + '-' + _lut[d2 >> 16 & 0xff] + _lut[d2 >> 24 & 0xff] +
      _lut[d3 & 0xff] + _lut[d3 >> 8 & 0xff] + _lut[d3 >> 16 & 0xff] + _lut[d3 >> 24 & 0xff];
  }

  /**
   * promiseRetry calls factory up to retries+1 times until the returned promise
   * resolves.
   * @param {PromiseConstructor} P
   * @param {function(): Promise} factory
   * @param {number} [retries=2]
   * @param {number} [retryBackoffMs=150]
   */
  function promiseRetry(P, factory, retries, retryBackoffMs) {
    if (!retries) retries = 2;
    var n = 0;
    return new P(function (resolve, reject) {
      return attempt(0);
      function attempt(delay) {
        return new P(function (r) {
          if (delay) setTimeout(r, delay); else r();
        })
        .then(function () {
          return factory(function retry(err) {
            throw { message: 'promise-retry', err: err };
          }, ++n);
        })
        .then(
          resolve,
          function (e) {
            if (e && e.message === 'promise-retry') {
              if (n - 1 < retries) {
                return attempt(delay + (retryBackoffMs || 150));
              }
              reject(e.err);
              return;
            }
            reject(e);
          }
        );
      }
    });
  }

  /**
   * The core module of ravelinjs provides device-identification, and is used by
   * other modules to communicate with the Ravelin API.
   */

  /**
   * @typedef {object} CoreConfig
   * @prop {string} [key] The API key ("publishable_key_..." or "pk_...") used to authenticate with the Ravelin API.
   * @prop {string} [api] A string of the format "https://api.ravelin.net" which forms the base of API requests.
   * @prop {string} [prefix=rjs-] A string of the default prefix for the deviceId.
   * @prop {string|Promise<string>} [id] An explicit deviceId to use. If the Promise errors or returns an empty value, Ravelin's own deviceId tracking kicks in.
   * @prop {string} [cookie=ravelinDeviceId] The name of the cookie that the deviceId is kept in.
   * @prop {string} [cookieDomain] The domain on which to set cookies.
   * @prop {string} [cookieSameSite] The SameSite attribute to control how cookies are sent in cross-site requests.
   * @prop {number} [cookieExpiryDays=365] The max lifetime of a deviceId, in days. Values <= 0 makes Ravelin treat the named cookie as read-only.
   * @prop {number} [syncMs=2000] The sync timeout frequency.
   * @prop {number} [sendRetryMs=150] The send retry backoff time.
   * @prop {PromiseConstructor} [Promise] The Promise implementation to use in ravelinjs's asynchronous functions.
   */

  /**
   * Core library instance. Provides helpers and device identification.
   * @class
   * @param {CoreConfig} cfg
   */
  function Core(cfg) {
    this.Promise = cfg.Promise;

    // API connectivity.
    this.version = cfg.version || "1.10.0-ravelinjs";
    this.key = cfg.key;
    this.api = cfg.api || apiFromKey(this.key);
    this.api = this.api[0] + this.api.substr(1).replace(/^\/+|\/$/g, '');
    this.sendRetryMs = cfg.sendRetryMs || 150;
    this.cookie = cfg.cookie || 'ravelinDeviceId';
    this.sessionCookie = cfg.sessionCookie || 'ravelinSessionId';
    this.cookieExpiryDays = cfg.cookieExpiryDays || 365;
    this.cookies = new CookieJar({
      domain: cfg.cookieDomain,
      sameSite: cfg.cookieSameSite
    });

    this.prefix = typeof cfg.prefix === 'string' ? cfg.prefix : 'rjs-';
    this._id = resolve(this.Promise, cfg.id);
    this.sync();
    if (cfg.init !== false) {
      this.attach(cfg.syncMs || 2000);
    }
  }

  /**
   * resolve returns a Promise that resolves to v. If v is already a promise, we
   * just return it.
   * @param {PromiseConstructor} Promise The promise implementation.
   * @param {Promise|any} v The value we want in a then.
   * @returns {Promise}
   */
  function resolve(Promise, v) {
    if (v && typeof(v.then) === 'function') {
      return v;
    }
    return new Promise(function(r) { r(v); });
  }

  /**
   * id reads our device ID from the browser.
   * @returns {Promise<string>}
   */
  Core.prototype.id = function() {
    return this.ids().then(function(ids) {
      return ids.device;
    });
  };

  /**
   * @typedef {Object} IDs
   * @prop {string} device
   * @prop {string} session
   */

  /**
   * ids reads all IDs from the browser.
   * @returns {Promise<IDs>}
   */
  Core.prototype.ids = function() {
    if (this._ids) {
      return this._ids;
    }

    var c = this;
    this._ids =
      this.sniffError(this._id)
      .then(undefined, function() { /* swallow error. */ })
      .then(function(cfgId) {
        var d = cfgId || c.cookies.get(c.cookie);
        var s = c.cookies.get(c.sessionCookie);

        if (!cfgId && s) {
          // Restore d from s.
          var sep = s.indexOf(':');
          if (sep != -1) {
            d = s.substr(0, sep);
          }

          // Strip d: from the beginning of s.
          if (d && hasPrefix(s, d+':')) {
            s = s.substr(d.length+1);
          }
        }

        return {
          device: d || c.prefix + uuid(),
          session: s || uuid()
        };
      });
    return this._ids;
  };

  /**
   * hasPrefix returns whether string s starts with string prefix.
   * @param {string} s
   * @param {string} prefix
   * @returns {boolean}
   */
  function hasPrefix(s, prefix) {
    return s.substr(0, prefix.length) === prefix;
  }

  /**
   * daysFromNow returns the current time, days in the future.
   * @param {number} days The number of days in the future.
   * @returns Date
   */
  function daysFromNow(days) {
    return new Date(new Date().getTime() + days * 86400 * 1000);
  }

  /**
   * sync writes our ID into the various places we try to keep hold of it.
   */
  Core.prototype.sync = function() {
    var c = this;
    return this.ids().then(function(ids) {
      if (c.cookieExpiryDays > 0) {
        c.cookies.set({
          name: c.cookie,
          value: ids.device,
          expires: daysFromNow(c.cookieExpiryDays)
        });
      }
      c.cookies.set({
        name: c.sessionCookie,
        value: ids.device + ":" + ids.session
      });
    });
  };

  /**
   * attach to the browser to maintain our ids somewhere.
   * @param {number} syncMs How often we attempt to re-synchronise.
   */
  Core.prototype.attach = function(syncMs) {
    setInterval(bind(this.sync, this), syncMs);
  };

  /**
   * sameOrigin returns whether a url is treated as the same origin of the page
   * loaded into the browser.
   * @param {string} url
   * @returns {boolean}
   */
  function sameOrigin(url) {
    var matches = url.match('(https?)?://([^/]+)');
    if (!matches) {
      // Relative paths must be the same origin.
      return true;
    }
    // Check the host:port matches.
    if (matches[2] != window.location.host) {
      return false;
    }
    // Check the scheme matches.
    if (matches[1] != "" && matches[1] != window.location.protocol.replace(/:/, '')) {
      return false;
    }
    return true;
  }

  var defaultAPI = 'https://live.ravelin.click';

  /**
   * apiFromKey returns the likely API URL based on the key. Keys are arbitrary
   * strings, but we occassionally issue keys of the format
   * "publishable_key_[test_][env_][key]" to embed env.
   * @param {string} key
   * @returns {string} API URL
   */
  function apiFromKey(key) {
    if (!key || key.substr(0, 16) != 'publishable_key_') {
      return defaultAPI;
    }

    var words = key.substr(16).split('_');
    if (words.length < 2) {
      return defaultAPI;
    }

    var env = words[words.length - 2];
    if (env == 'test' || env == 'live') {
      return defaultAPI;
    }

    return 'https://' + encodeURIComponent(env) + '.ravelin.click';
  }

  var _XDomainRequest = typeof XDomainRequest !== 'undefined' && XDomainRequest;

  /**
   * @typedef {Object} CoreResponse
   * @property {number} status
   * @property {string} text
   * @property {number} attempt
   */

  /**
   * reportError sends an Error to Ravelin so that we can track what's happening.
   * @param {*} e
   * @returns {Promise<void>}
   */
  Core.prototype.reportError = function(e) {
    var that = this;
    if (this._ids) {
      return this._ids
        .then(undefined, function() { /* swallow error. */ })
        .then(report);
    }
    return report();

    function report(ids) {
      return that.send('POST', 'z/err', {
        deviceId: ids && ids.device,
        libVer: that.version,
        type: e.name,
        msg: e.message || e,
        error: e.stack
      }).then(function() {});
    }
  };

  /**
   * sniffError invokes fn and reports any errors back to Ravelin. If the return
   * value of fn is a Promise-like object then any rejection is also reported.
   */
  Core.prototype.sniffError = function(fn) {
    var c = this;
    try {
      var p = typeof fn === 'function' ? fn() : fn;
      if (p && p.then) p.then(undefined, function(e) {
        c.reportError(e);
      });
      return p;
    } catch (e) {
      c.reportError(e);
      throw e;
    }
  };

  /**
   * bind returns a function that invokes fn.apply(thisArg, ...args) and reports
   * any errors using sniffError.
   *
   * @param {Function} fn
   * @param {*} [thisArg]
   * @param {...*} [args]
   * @returns {Function}
   */
  Core.prototype.bind = function() {
    var c = this;
    var fn = bind.apply(null, Array.prototype.slice.call(arguments));
    return function() {
      var args = Array.prototype.slice.call(arguments);
      return c.sniffError(function() {
        return fn.apply(null, args);
      });
    };
  };

  /**
   * send makes a HTTP request and returns a Promise that resolves to the response
   * body or is rejected with any errors or non-2xx status codes.
   *
   * For backwards compatibility with IE8-9 whose XDomainRequest does not allow us
   * to add headers to cross-domain requests - and to avoid OPTIONS preflight
   * requests on browsers that do - we do not accept setting headers here. Instead
   * we put the key in the query string and expect the server to support whatever
   * headers the browser cares to add.
   *
   * @param {string} method
   * @param {string} path
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype.send = function(method, path, body) {
    if (!this.key) {
      throw new Error('ravelin/core: no key set for API requests');
    }

    // Build the URL to run.
    var url = this.api + '/' + path.replace(/^\/+/, '');
    url += (url.indexOf('?') == -1 ? '?' : '&') + 'key=' + encodeURIComponent(this.key);
    url = url.replace(/^\/+/, '/');

    var c = this;
    return promiseRetry(c.Promise, function(retry, attempt) {
      return (
        // Make the request.
        !sameOrigin(url) && _XDomainRequest) ?
          c._sendXDR(method, url, stringify(body)) :
          c._sendXHR(method, url, stringify(body)
      ).then(function(r) {
        r.attempt = attempt;

        // Resolve/reject based on status.
        if (r.status == 1223) {
          r.status = 204; // IE0013 from http://www.enhanceie.com/ie/bugs.asp.
        }
        if (r.status >= 200 && r.status < 300) {
          return r;
        } else if (r.status == 400 && r.text == "") {
          // Retry empty 400s injected by the BrowserStack tunnel.
          retry(r);
        } else if (r.status >= 500) {
          // Retry errors.
          retry(r);
        } else if (!r.status) {
          // Retry connection errors.
          retry(r);
        }
        return new c.Promise(function(resolve, reject) {
          reject(r);
        });
      });
    }, 2, this.sendRetryMs)
    .then(undefined, function(r) {
      if (r.attempt) {
        // Convert the response format into an error.
        throw new Error("ravelin/core: " + method + " " + url + " attempt " + r.attempt + " returned status " + r.status + (r.text ? " and body " + r.text : ''));
      }
      throw r;
    });
  };

  /**
   * _sendXHR wraps an XHR in a Promise.
   *
   * @param {string} method
   * @param {string} url
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype._sendXHR = function(method, url, body) {
    return new this.Promise(function(resolve) {
      var r = new XMLHttpRequest();
      r.onreadystatechange = function() {
        if (r.readyState == 4) {
          resolve({status: r.status, text: r.responseText});
        }
      };

      // Send the request.
      r.open(method, url);
      r.send(body);
    });
  };

  /**
   * _sendXDR wraps an XHR in a Promise.
   *
   * @param {string} method
   * @param {string} url
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype._sendXDR = function(method, url, body) {
    return new this.Promise(function(resolve, reject) {
      var r = new _XDomainRequest();
      r.onload = function() {
        resolve({status: r.status || 200, text: r.responseText});
      };
      r.onerror = function() {
        reject({status: r.status, text: r.responseText});
      };
      r.onprogress = function() {
        // Needed to ensure onload fires.
      };

      r.open(method, url);
      setTimeout(function () {
        // Wrapped in a timeout to prevent an issue with the interface where some
        // requests are lost if multiple XDomainRequests are being sent at the
        // same time.
        // https://developer.mozilla.org/en-US/docs/Web/API/XDomainRequest.
        r.send(body);
      }, 0);
    });
  };

  /**
   * stringify is JSON.stringify with prototype safety.
   *
   * @param {object} obj
   * @returns {string}
   */
  function stringify(obj) {
    if (Array.prototype.toJSON) {
      // https://stackoverflow.com/questions/710586/json-stringify-array-bizarreness-with-prototype-js
      var _array_tojson = Array.prototype.toJSON;
      delete Array.prototype.toJSON;
      var str = JSON.stringify(obj);
      /* jshint -W121 */// We're restoring a native extension.
      Array.prototype.toJSON = _array_tojson;
      /* jshint +W121 */
      return str;
    }
    return JSON.stringify(obj);
  }

  /**
    * @param {object} [cfg]
    */
  function Ravelin(cfg) {
    cfg.Promise = cfg.Promise || Ravelin.Promise;
    this.core = new Core(cfg);
  }

  Ravelin.Promise = typeof Promise !== 'undefined' && Promise;

  return Ravelin;

}));
